import { IOperationResultNoValue } from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useModal } from '../../../../../models/hooks/useModal';
import {
	AccountDetailsContext,
	AccountDetailsSettingsContext,
	AdminPermission,
	AdminUserSessionContext,
} from '../../../../../viewmodels/AdminViewModels';
import { IDonationDataboardTagSettings } from '../../../../../viewmodels/AppViewModels';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { Modal } from '../../../../../web/components/Modal';
import { TextInput } from '../../../../../web/components/TextInput';
import { Toggle } from '../../../../../web/components/Toggle';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../../../../web/components/HoverCard';
import { InfoIcon } from '../../../../../web/components/svgs/icons/InfoIcon';
import { KeyDateIcon } from '../../../../../web/components/svgs/icons/KeyDateIcon';
import { TrashIcon } from '../../../../../web/components/svgs/icons/TrashIcon';
import { WarningIconSmall, WarningIconType } from '../../../../../web/components/svgs/icons/WarningIcon';
import { baseStyleSheet } from '../../../../../web/styles/styles';
import { ConditionalAccess } from '../../../../components/ConditionalAccess';
import { DeleteKeyFactsModal } from '../../../../components/DeleteKeyFactsModal';
import { RestoreKeyFactsModal } from '../../../../components/RestoreKeyFactsModal';
import { SettingCategory } from './Setting';
import { ArchiveAllDonationsConfirmationOptions } from './options';
import { SettingsColumns } from './presentation';
import { styleSheet } from './styles';

interface IProps extends RouteComponentProps<any> {
	className?: string;
	scrollToBottomPortalId?: string;
	styles?: StyleDeclarationValue[];
}

const SettingsFc: React.FC<IProps> = () => {
	const [settingsSearch, setSettingsSearch] = useState('');
	const [showRestoreKeyFactsModal, setShowRestoreKeyFactsModal] = useState(false);
	const [showDeleteKeyFactsModal, setShowDeleteKeyFactsModal] = useState(false);
	const adminUserSession = useUserSession<AdminUserSessionContext>();

	const accountDetailsContext = React.useContext(AccountDetailsContext);

	const canModifyAccount = !!adminUserSession.hasPermission(AdminPermission.ModifyAccount);
	const hasDevPermission = !!adminUserSession.hasPermission(AdminPermission.OnlyForDevelopers);
	const hasSupportPermission = !!adminUserSession.hasPermission(AdminPermission.SupportLevel3);

	const errorMessages = useErrorMessages();

	const deleteAllDonationsModal = useModal(
		false,

		(result: IConfirmationDialogOption<boolean>) => {
			if (result?.isDestructive) {
				accountDetailsContext.accountOperations
					.deleteAllDonations()
					?.then(() => {
						toast.success('All donations archived successfully');
					})
					?.catch((error: IOperationResultNoValue) => {
						errorMessages.pushApiError(error);
					});
			}
		},
		[accountDetailsContext.accountOperations]
	);

	const settingsContext = React.useContext(AccountDetailsSettingsContext);

	const [donationTagModalOpen, setDonationTagModalOpen] = useState(false);
	const [pendingDonationTagSettings, setPendingDonationTagSettings] = useState<
		Omit<IDonationDataboardTagSettings, '_type'>
	>({
		enabled: true,
		lastDonationTagsEnabled: true,
		lifetimeDonationTagsEnabled: true,
		yearlyDonationTagsEnabled: true,
	});

	const openDonationTagModal = () => {
		const tagSettings = accountDetailsContext.accountOperations.account?.features?.boards?.donationBoard?.configuration
			?.tagSettings as IDonationDataboardTagSettings | undefined;
		setPendingDonationTagSettings({
			enabled: tagSettings?.enabled ?? true,
			lastDonationTagsEnabled: tagSettings?.lastDonationTagsEnabled ?? true,
			lifetimeDonationTagsEnabled: tagSettings?.lifetimeDonationTagsEnabled ?? true,
			yearlyDonationTagsEnabled: tagSettings?.yearlyDonationTagsEnabled ?? true,
		});
		setDonationTagModalOpen(true);
	};

	const closeDonationTagModal = () => {
		setDonationTagModalOpen(false);
	};

	const saveDonationTagSettings = () => {
		settingsContext.onDonationTagSettingsChanged?.(pendingDonationTagSettings);
		setDonationTagModalOpen(false);
	};

	const onClearSettingsSearch = () => {
		setSettingsSearch('');
	};

	const onSettingsSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSettingsSearch(e.target.value);
	};
	const onRestoreKeyFacts = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setShowRestoreKeyFactsModal(true);
	};

	const onRestoreKeyFactsRequestClose = () => {
		setShowRestoreKeyFactsModal(false);
	};

	const onDeleteKeyFacts = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		setShowDeleteKeyFactsModal(true);
	};

	const onDeleteKeyFactsRequestClose = () => {
		setShowDeleteKeyFactsModal(false);
	};

	return (
		<>
			{accountDetailsContext.accountOperations.account ? (
				<>
					<ConditionalAccess permission={AdminPermission.ModifyAccount}>
						<div className={css(styleSheet.deleteKeyFactsHeader)}>
							<div className={css(styleSheet.settingsSearchContainer)}>
								<TextInput
									className={css(styleSheet.settingsSearch)}
									inputId='admin-settings-search'
									onChange={onSettingsSearch}
									placeholder='Search Settings'
									type='text'
									value={settingsSearch}
								/>
								{settingsSearch?.length > 1 ? (
									<button className={css(baseStyleSheet.brandLink)} onClick={onClearSettingsSearch}>
										Clear
									</button>
								) : null}
							</div>
							<div className={css(styleSheet.buttonsWrapper)}>
								<div>
									<button className={`${css(styleSheet.actionEntities)}`} onClick={onRestoreKeyFacts}>
										<KeyDateIcon className={css(styleSheet.columnButtonIcon)} />
										<span style={{ marginLeft: 10 }}>Restore Key Facts</span>
									</button>

									<RestoreKeyFactsModal
										userSession={adminUserSession}
										modalProps={{
											isOpen: showRestoreKeyFactsModal,
											onRequestClose: onRestoreKeyFactsRequestClose,
										}}
									/>
									<button className={`${css(styleSheet.actionEntities)}`} onClick={onDeleteKeyFacts}>
										<TrashIcon className={css(styleSheet.columnButtonIcon)} />
										<span style={{ marginLeft: 10 }}>Delete Key Facts</span>
									</button>
									<DeleteKeyFactsModal
										userSession={adminUserSession}
										modalProps={{
											isOpen: showDeleteKeyFactsModal,
											onRequestClose: onDeleteKeyFactsRequestClose,
										}}
									/>
								</div>
							</div>
						</div>
					</ConditionalAccess>
					<div className={`${css(styleSheet.overviewContainer)}`}>
						<SettingsColumns
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.Administrative}
						/>
						<SettingsColumns
							hasSupportPermission={hasSupportPermission}
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.Features}
						/>
						<SettingsColumns
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.EmailTracking}
						/>
						<SettingsColumns
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.Dashboard}
						/>
						<SettingsColumns
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.PreApprovedContent}
						/>
						<SettingsColumns
							hasSupportPermission={hasSupportPermission}
							hasDevPermission={hasDevPermission}
							canModifyAccount={canModifyAccount}
							settingsSearch={settingsSearch}
							category={SettingCategory.Databoards}
							onClearAllDonations={hasSupportPermission ? deleteAllDonationsModal.setIsOpen(true) : undefined}
							onOpenDonationTagSettings={openDonationTagModal}
						/>
						<ConditionalAccess
							permission={AdminPermission.OnlyForDevelopers}
							or={[AdminPermission.SupportLevel3, AdminPermission.ResetAccount]}
						>
							<SettingsColumns
								hasDevPermission={hasDevPermission}
								hasSupportPermission={hasSupportPermission}
								canModifyAccount={canModifyAccount}
								settingsSearch={settingsSearch}
								category={SettingCategory.Permanent}
							/>
						</ConditionalAccess>
					</div>
					<ConditionalAccess permission={AdminPermission.SupportLevel3}>
						<ConfirmationDialog
							className={css(styleSheet.deleteRenewKeyFactsModal)}
							icon={<WarningIconSmall type={WarningIconType.Red} />}
							modalProps={{
								isOpen: deleteAllDonationsModal.isOpen,
								onRequestClose: deleteAllDonationsModal.onRequestClose,
							}}
							options={ArchiveAllDonationsConfirmationOptions}
							title='Archive All Donations?'
						>
							<div>
								<p>Are you sure you want to archive all donations for this account?</p>
							</div>
						</ConfirmationDialog>
						{accountDetailsContext.accountOperations.isDeletingAllDonations && (
							<LoadingSpinner className={css(styleSheet.loading)} type='large' />
						)}
					</ConditionalAccess>
					<Modal isOpen={donationTagModalOpen} onRequestClose={closeDonationTagModal} useDefaultHeader>
						<div className={css(styleSheet.donationTagModal)}>
							<div className={css(styleSheet.donationTagModalTitle)}>Donation Tag Settings</div>
							<div className={css(styleSheet.donationTagModalRow)}>
								<span>Donation Tagging</span>
								<Toggle
									id='donation-tagging-enabled'
									isOn={pendingDonationTagSettings.enabled}
									onToggleCheckChanged={checked =>
										setPendingDonationTagSettings(prev => ({ ...prev, enabled: checked }))
									}
									uncheckedColor='#ccc'
								/>
							</div>
							{!pendingDonationTagSettings.enabled && (
								<p className={css(styleSheet.donationTagWarning)}>
									Disabling will remove all existing donation tags from contacts.
								</p>
							)}
							<div className={css(styleSheet.donationTagModalRow)}>
								<span className={css(styleSheet.donationTagModalLabel)}>
									Last Donation Tags
									<HoverCard>
										<HoverCardTrigger className={css(styleSheet.donationTagModalInfoIcon)}>
											<InfoIcon />
										</HoverCardTrigger>
										<HoverCardContent styles={[styleSheet.donationTagModalHoverContent]}>
											Creates &quot;Last Donation: &lt;amount&gt;&quot; tags
										</HoverCardContent>
									</HoverCard>
								</span>
								<Toggle
									id='last-donation-tags'
									disabled={!pendingDonationTagSettings.enabled}
									isOn={pendingDonationTagSettings.lastDonationTagsEnabled}
									onToggleCheckChanged={checked =>
										setPendingDonationTagSettings(prev => ({
											...prev,
											lastDonationTagsEnabled: checked,
										}))
									}
									uncheckedColor='#ccc'
								/>
							</div>
							<div className={css(styleSheet.donationTagModalRow)}>
								<span className={css(styleSheet.donationTagModalLabel)}>
									Lifetime Donation Tags
									<HoverCard>
										<HoverCardTrigger className={css(styleSheet.donationTagModalInfoIcon)}>
											<InfoIcon />
										</HoverCardTrigger>
										<HoverCardContent styles={[styleSheet.donationTagModalHoverContent]}>
											Creates &quot;Lifetime Donations: &lt;amount&gt;&quot; tags
										</HoverCardContent>
									</HoverCard>
								</span>
								<Toggle
									id='lifetime-donation-tags'
									disabled={!pendingDonationTagSettings.enabled}
									isOn={pendingDonationTagSettings.lifetimeDonationTagsEnabled}
									onToggleCheckChanged={checked =>
										setPendingDonationTagSettings(prev => ({
											...prev,
											lifetimeDonationTagsEnabled: checked,
										}))
									}
									uncheckedColor='#ccc'
								/>
							</div>
							<div className={css(styleSheet.donationTagModalRow)}>
								<span className={css(styleSheet.donationTagModalLabel)}>
									Yearly Donation Tags
									<HoverCard>
										<HoverCardTrigger className={css(styleSheet.donationTagModalInfoIcon)}>
											<InfoIcon />
										</HoverCardTrigger>
										<HoverCardContent styles={[styleSheet.donationTagModalHoverContent]}>
											Creates &quot;YYYY Donations: &lt;amount&gt;&quot; tags
										</HoverCardContent>
									</HoverCard>
								</span>
								<Toggle
									id='yearly-donation-tags'
									disabled={!pendingDonationTagSettings.enabled}
									isOn={pendingDonationTagSettings.yearlyDonationTagsEnabled}
									onToggleCheckChanged={checked =>
										setPendingDonationTagSettings(prev => ({
											...prev,
											yearlyDonationTagsEnabled: checked,
										}))
									}
									uncheckedColor='#ccc'
								/>
							</div>
							<p className={css(styleSheet.donationTagInfo)}>
								Disabling a tag type will remove existing tags of that type on the next donation update.
							</p>
							<div className={css(styleSheet.donationTagModalFooter)}>
								<button className={css(baseStyleSheet.ctaButton)} onClick={saveDonationTagSettings}>
									Save
								</button>
								<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={closeDonationTagModal}>
									Cancel
								</button>
							</div>
						</div>
					</Modal>
				</>
			) : (
				<LoadingSpinner className={css(styleSheet.loading)} type='large' />
			)}
		</>
	);
};

const SettingsWithObserver = observer(SettingsFc);
export const Settings = withRouter(SettingsWithObserver);
